import { IconMail } from "@tabler/icons-react"
import { type FC, useState } from "react"
import { Link, useNavigate } from "react-router"

import { getPasswordValidationError } from "@forento/core/utilities/password"
import { cn } from "@forento/ui/utilities/tailwind"

import { Input, PasswordInput } from "~/components/input"
import { useUserContext } from "~/context/user"
import { query } from "~/utilities/trpc"

const passwordRequirements =
	"Använd 10-128 tecken med minst en bokstav och en siffra. Börja eller sluta inte med mellanslag."

const SignUpPage: FC = () => {
	const userContext = useUserContext()
	const navigate = useNavigate()

	const [email, setEmail] = useState("")
	const [password, setPassword] = useState("")
	const [confirmPassword, setConfirmPassword] = useState("")
	const [firstName, setFirstName] = useState("")
	const [lastName, setLastName] = useState("")
	const [error, setError] = useState<string>()

	const signUp = query.signUp.useMutation({
		onMutate() {
			setError(undefined)
		},
		async onSuccess({ status }) {
			if (status === "email-exists") {
				setError("E-postadressen finns redan registrerad")
				return
			}

			await userContext.reload({ clearSessionCache: true })
			navigate("/courses")
		},
		onError(error) {
			console.error(error)
		},
	})

	return (
		<div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-12">
			<div className="w-full max-w-md">
				<div className="mb-8 text-center">
					<h1 className="text-2xl font-bold text-slate-900">Skapa ett konto</h1>
					<p className="mt-2 text-sm text-slate-600">
						Välkommen! Ange dina uppgifter för att skapa ett konto.
					</p>
				</div>

				{error && <div className="mb-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">{error}</div>}

				<form
					onSubmit={event => {
						event.preventDefault()

						if (password !== confirmPassword) {
							setError("Lösenorden matchar inte")
							return
						}
						if (getPasswordValidationError(password) !== null) {
							setError(passwordRequirements)
							return
						}

						signUp.mutate({ email, password, firstName, lastName })
					}}
					className="space-y-6"
				>
					<Input
						type="text"
						label="Förnamn"
						value={firstName}
						onChange={setFirstName}
						placeholder="Ange ditt förnamn"
						autoComplete="given-name"
						required
					/>

					<Input
						type="text"
						label="Efternamn"
						value={lastName}
						onChange={setLastName}
						placeholder="Ange ditt efternamn"
						autoComplete="family-name"
						required
					/>

					<Input
						type="email"
						label="E-postadress"
						value={email}
						onChange={setEmail}
						placeholder="Ange din e-postadress"
						autoComplete="email"
						icon={IconMail}
						required
					/>

					<PasswordInput
						label="Lösenord"
						value={password}
						onChange={setPassword}
						placeholder="Ange ditt lösenord"
						autoComplete="new-password"
					/>
					<p className="-mt-4 text-xs leading-relaxed text-slate-500">{passwordRequirements}</p>

					<PasswordInput
						label="Bekräfta lösenord"
						value={confirmPassword}
						onChange={setConfirmPassword}
						placeholder="Bekräfta ditt lösenord"
						autoComplete="new-password"
					/>

					<button
						type="submit"
						disabled={signUp.isPending}
						className={cn(
							"w-full rounded-lg px-4 py-3 text-sm font-semibold text-white transition-colors duration-200",
							signUp.isPending
								? "cursor-not-allowed bg-slate-400"
								: "bg-primary hover:bg-primary/90 focus:ring-primary cursor-pointer focus:ring-2 focus:ring-offset-2 focus:outline-none",
						)}
					>
						{signUp.isPending ? "Skapar konto..." : "Skapa konto"}
					</button>

					<div className="relative">
						<div className="absolute inset-0 flex items-center">
							<div className="w-full border-t border-slate-300" />
						</div>
						<div className="relative flex justify-center text-xs uppercase">
							<span className="bg-slate-50 px-2 text-slate-500">eller</span>
						</div>
					</div>

					<div className="text-center">
						<p className="text-sm text-slate-600">
							Har du redan ett konto?{" "}
							<Link
								to="/user/sign-in"
								className="text-primary hover:text-primary/80 font-medium hover:underline"
							>
								Logga in
							</Link>
						</p>
					</div>
				</form>
			</div>
		</div>
	)
}

export default SignUpPage
